import React, { useState, useEffect } from 'react';
import { 
  Save, Plus, Trash2, GripVertical, Loader2, Eye, EyeOff, RotateCcw,
  Link as LinkIcon, Search, Heart, MessageSquare, User, Settings, Crown,
  CreditCard, HandHeart, Info, HelpCircle, Shield, FileText, Mail, Bookmark,
  Home, Star, Bell, Calendar, MapPin, Image, Gift, Users, Menu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Available icons
const ICON_OPTIONS = [
  { value: 'Search', label: 'Search', icon: Search },
  { value: 'Heart', label: 'Heart', icon: Heart },
  { value: 'MessageSquare', label: 'Messages', icon: MessageSquare },
  { value: 'User', label: 'User', icon: User },
  { value: 'Settings', label: 'Settings', icon: Settings },
  { value: 'Crown', label: 'Crown', icon: Crown },
  { value: 'CreditCard', label: 'Credit Card', icon: CreditCard },
  { value: 'HandHeart', label: 'Donate', icon: HandHeart },
  { value: 'Info', label: 'Info', icon: Info },
  { value: 'HelpCircle', label: 'Help', icon: HelpCircle },
  { value: 'Shield', label: 'Shield', icon: Shield },
  { value: 'FileText', label: 'Document', icon: FileText },
  { value: 'Mail', label: 'Mail', icon: Mail },
  { value: 'Bookmark', label: 'Bookmark', icon: Bookmark },
  { value: 'Home', label: 'Home', icon: Home },
  { value: 'Star', label: 'Star', icon: Star },
  { value: 'Bell', label: 'Bell', icon: Bell },
  { value: 'Calendar', label: 'Calendar', icon: Calendar },
  { value: 'MapPin', label: 'Location', icon: MapPin },
  { value: 'Image', label: 'Image', icon: Image },
  { value: 'Gift', label: 'Gift', icon: Gift },
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'Link', label: 'Link', icon: LinkIcon },
];

const DEFAULT_MAIN_NAV = [
  { id: 'search', label: 'Search Matches', url: '/search', icon: 'Search', iconColor: '#10b981', enabled: true, order: 0, requiresAuth: true, showBadge: false },
  { id: 'shortlist', label: 'Shortlist', url: '/shortlist', icon: 'Bookmark', iconColor: '#10b981', enabled: true, order: 1, requiresAuth: true, showBadge: false },
  { id: 'interests', label: 'Interests', url: '/interests', icon: 'Heart', iconColor: '#f87171', enabled: true, order: 2, requiresAuth: true, showBadge: true },
  { id: 'messages', label: 'Messages', url: '/messages', icon: 'MessageSquare', iconColor: '#10b981', enabled: true, order: 3, requiresAuth: true, showBadge: true },
];

const DEFAULT_MORE_MENU = [
  { id: 'profile', label: 'My Profile', url: '/profile', icon: 'User', iconColor: '#6b7280', enabled: true, order: 0, requiresAuth: true, showBadge: false },
  { id: 'account', label: 'Account Settings', url: '/account-settings', icon: 'Settings', iconColor: '#6b7280', enabled: true, order: 1, requiresAuth: true, showBadge: false },
  { id: 'subscription', label: 'Subscription', url: '/subscription', icon: 'Crown', iconColor: '#eab308', enabled: true, order: 2, requiresAuth: true, showBadge: false },
  { id: 'pricing', label: 'Premium Plans', url: '/pricing', icon: 'CreditCard', iconColor: '#6b7280', enabled: true, order: 3, requiresAuth: false, showBadge: false },
  { id: 'donate', label: 'Support Qbool', url: '/donate', icon: 'HandHeart', iconColor: '#f87171', enabled: true, order: 4, requiresAuth: false, showBadge: false },
  { id: 'about', label: 'About Us', url: '/about', icon: 'Info', iconColor: '#6b7280', enabled: true, order: 5, requiresAuth: false, showBadge: false },
  { id: 'help', label: 'Help Center', url: '/help', icon: 'HelpCircle', iconColor: '#6b7280', enabled: true, order: 6, requiresAuth: false, showBadge: false },
  { id: 'privacy', label: 'Privacy Policy', url: '/privacy', icon: 'Shield', iconColor: '#6b7280', enabled: true, order: 7, requiresAuth: false, showBadge: false },
  { id: 'terms', label: 'Terms of Service', url: '/terms', icon: 'FileText', iconColor: '#6b7280', enabled: true, order: 8, requiresAuth: false, showBadge: false },
  { id: 'contact', label: 'Contact Us', url: '/contact', icon: 'Mail', iconColor: '#6b7280', enabled: true, order: 9, requiresAuth: false, showBadge: false },
];

const getIconComponent = (iconName) => {
  const iconOption = ICON_OPTIONS.find(opt => opt.value === iconName);
  return iconOption ? iconOption.icon : LinkIcon;
};

const AdminNavigationMenu = () => {
  const [mainNav, setMainNav] = useState(DEFAULT_MAIN_NAV);
  const [moreMenu, setMoreMenu] = useState(DEFAULT_MORE_MENU);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('main');
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/navigation-menu`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.main_nav?.length > 0) {
          setMainNav(data.main_nav.sort((a, b) => a.order - b.order));
        }
        if (data.more_menu?.length > 0) {
          setMoreMenu(data.more_menu.sort((a, b) => a.order - b.order));
        }
      }
    } catch (error) {
      console.error('Failed to load navigation settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/navigation-menu`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ main_nav: mainNav, more_menu: moreMenu })
      });

      if (response.ok) {
        toast({
          title: "Saved!",
          description: "Navigation menu updated successfully.",
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save navigation settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setMainNav(DEFAULT_MAIN_NAV);
    setMoreMenu(DEFAULT_MORE_MENU);
    toast({
      title: "Reset",
      description: "Navigation reset to default. Click Save to apply.",
    });
  };

  const addItem = (type) => {
    const newItem = {
      id: `item_${Date.now()}`,
      label: 'New Item',
      url: '/',
      icon: 'Link',
      iconColor: '#10b981',
      enabled: true,
      order: type === 'main' ? mainNav.length : moreMenu.length,
      requiresAuth: false,
      showBadge: false
    };

    if (type === 'main') {
      setMainNav([...mainNav, newItem]);
    } else {
      setMoreMenu([...moreMenu, newItem]);
    }
  };

  const removeItem = (type, id) => {
    if (type === 'main') {
      setMainNav(mainNav.filter(item => item.id !== id));
    } else {
      setMoreMenu(moreMenu.filter(item => item.id !== id));
    }
  };

  const updateItem = (type, id, field, value) => {
    const updateFn = (items) => items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    );

    if (type === 'main') {
      setMainNav(updateFn(mainNav));
    } else {
      setMoreMenu(updateFn(moreMenu));
    }
  };

  const moveItem = (type, fromIndex, toIndex) => {
    const items = type === 'main' ? [...mainNav] : [...moreMenu];
    const [movedItem] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, movedItem);
    
    // Update order values
    items.forEach((item, i) => item.order = i);
    
    if (type === 'main') {
      setMainNav(items);
    } else {
      setMoreMenu(items);
    }
  };

  const renderMenuItem = (item, index, type) => {
    const IconComponent = getIconComponent(item.icon);
    const iconStyle = item.iconColor ? { color: item.iconColor } : {};
    
    return (
      <div
        key={item.id}
        className={`p-4 bg-white border rounded-lg space-y-3 ${!item.enabled ? 'opacity-60' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
            <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
              <IconComponent className="w-4 h-4" style={iconStyle} />
            </div>
            <span className="font-medium">{item.label}</span>
            {!item.enabled && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Hidden</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <Button variant="ghost" size="sm" onClick={() => moveItem(type, index, index - 1)}>
                ↑
              </Button>
            )}
            {index < (type === 'main' ? mainNav.length : moreMenu.length) - 1 && (
              <Button variant="ghost" size="sm" onClick={() => moveItem(type, index, index + 1)}>
                ↓
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeItem(type, item.id)}
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Label</Label>
            <Input
              value={item.label}
              onChange={(e) => updateItem(type, item.id, 'label', e.target.value)}
              placeholder="Menu Label"
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">URL</Label>
            <Input
              value={item.url}
              onChange={(e) => updateItem(type, item.id, 'url', e.target.value)}
              placeholder="/page-url"
              className="h-9"
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Icon</Label>
            <Select
              value={item.icon}
              onValueChange={(value) => updateItem(type, item.id, 'icon', value)}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ICON_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <opt.icon className="w-4 h-4" />
                      {opt.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs">Icon Color</Label>
            <div className="flex gap-1">
              <input
                type="color"
                value={item.iconColor || '#10b981'}
                onChange={(e) => updateItem(type, item.id, 'iconColor', e.target.value)}
                className="w-9 h-9 rounded cursor-pointer border-0 p-0"
              />
              <Input
                value={item.iconColor || '#10b981'}
                onChange={(e) => updateItem(type, item.id, 'iconColor', e.target.value)}
                className="h-9 text-xs font-mono flex-1"
                placeholder="#10b981"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2 pt-5">
            <Switch
              checked={item.enabled}
              onCheckedChange={(checked) => updateItem(type, item.id, 'enabled', checked)}
            />
            <span className="text-sm">Visible</span>
          </div>

          <div className="flex items-center gap-2 pt-5">
            <Switch
              checked={item.requiresAuth}
              onCheckedChange={(checked) => updateItem(type, item.id, 'requiresAuth', checked)}
            />
            <span className="text-sm">Login Required</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Navigation Menu</h1>
          <p className="text-gray-600">Customize header navigation links</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-mint-500 hover:bg-mint-600">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Changes</>
            )}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('main')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'main'
              ? 'border-mint-500 text-mint-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Main Navigation ({mainNav.filter(i => i.enabled).length})
        </button>
        <button
          onClick={() => setActiveTab('more')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'more'
              ? 'border-mint-500 text-mint-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          "More" Dropdown ({moreMenu.filter(i => i.enabled).length})
        </button>
      </div>

      {/* Main Navigation */}
      {activeTab === 'main' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Menu className="w-5 h-5" />
                Main Navigation Links
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">Links shown in the header (logged-in users)</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => addItem('main')}>
              <Plus className="w-4 h-4 mr-1" /> Add Item
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {mainNav.map((item, index) => renderMenuItem(item, index, 'main'))}
            {mainNav.length === 0 && (
              <p className="text-center text-gray-500 py-8">No items. Click "Add Item" to create one.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* More Menu */}
      {activeTab === 'more' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Menu className="w-5 h-5" />
                "More" Dropdown Menu
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">Links inside the "More" dropdown</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => addItem('more')}>
              <Plus className="w-4 h-4 mr-1" /> Add Item
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {moreMenu.map((item, index) => renderMenuItem(item, index, 'more'))}
            {moreMenu.length === 0 && (
              <p className="text-center text-gray-500 py-8">No items. Click "Add Item" to create one.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-mint-100 rounded flex items-center justify-center">
                  <Heart className="w-5 h-5 text-mint-600" />
                </div>
                <span className="font-bold text-lg">qbool</span>
              </div>
              
              <div className="flex items-center gap-6">
                {mainNav.filter(i => i.enabled).map(item => {
                  const Icon = getIconComponent(item.icon);
                  const iconStyle = item.iconColor ? { color: item.iconColor } : {};
                  return (
                    <div key={item.id} className="flex items-center gap-1 text-gray-700 text-sm">
                      <Icon className="w-4 h-4" style={iconStyle} />
                      {item.label}
                    </div>
                  );
                })}
                <div className="flex items-center gap-1 text-gray-700 text-sm border-l pl-4">
                  More ▼
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Note: Changes here give you control but won&apos;t automatically update the website navbar until implemented.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNavigationMenu;
